import type { AdminViewStatsDto } from '@carcheck/shared';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { LockedTile } from './LockedTile';
import { TopCompaniesTile } from './TopCompaniesTile';
import { TopListingsTile } from './TopListingsTile';
import { ViewsTotalsTile } from './ViewsTotalsTile';
import { RequirePermission } from '../../../auth';

/**
 * AUT-977 platform view summary: three `view_analytics`-gated tiles from one
 * `GET /admin/view-stats` fetch. Each tile is a direct grid child under its
 * own `RequirePermission` with a same-size `LockedTile` fallback.
 */
export interface ViewsSectionProps {
  stats: AdminViewStatsDto | undefined;
  isLoading: boolean;
  isError: boolean;
}

const FULL = 'span 12';
const HALF = 'span 6';

export const ViewsSection = ({ stats, isLoading, isError }: ViewsSectionProps): ReactElement => {
  const { t } = useTranslation();
  const state = { data: stats, isLoading, isError };
  return (
    <>
      <RequirePermission
        perm="view_analytics"
        fallback={<LockedTile label={t('dashboard.views.title')} gridColumn={FULL} />}
      >
        <ViewsTotalsTile {...state} gridColumn={FULL} />
      </RequirePermission>
      <RequirePermission
        perm="view_analytics"
        fallback={<LockedTile label={t('dashboard.views.topListings')} gridColumn={HALF} />}
      >
        <TopListingsTile {...state} gridColumn={HALF} />
      </RequirePermission>
      <RequirePermission
        perm="view_analytics"
        fallback={<LockedTile label={t('dashboard.views.topCompanies')} gridColumn={HALF} />}
      >
        <TopCompaniesTile {...state} gridColumn={HALF} />
      </RequirePermission>
    </>
  );
};
